import { t } from '@lingui/core/macro'
import { sanitizeSensorId } from 'features/timeSeries/utils/dataProcessing'
import type { TimeSeriesDerivedValueEntryJSON } from 'types'
import { RolodexEntryType } from 'types'

import type { AgentModel } from './useAgentModels'

/**
 * Turn an agent model's KPI into a DERIVED PEN so the Process Data chart can
 * draw it — and hang the model's ±band on it exactly the way a sentinel's band
 * hangs on its target tag.
 *
 * Sentinels and the Experiment Lab never needed this: their target IS a
 * historian tag, so the pen already exists. An agent KPI (isentropic
 * efficiency, heat-recovery share, …) is a formula over several tags, which the
 * chart can only plot as a derived value.
 *
 * The agent writes its expression over ROLE names (`ctd`, `ctim`, `cpd`); the
 * chart evaluates over sanitized tag ids. Substituting role → sanitized tag id
 * is the whole translation.
 */

/**
 * Functions the chart's math instance can evaluate, mirroring `forecastMath`'s
 * dependency list.
 *
 * The agent authors physics in Python (its evaluator is a whitelisted Python
 * AST); the chart evaluates with math.js. They agree on arithmetic and not on
 * everything else, and anything math.js cannot evaluate produces no series at
 * all — which looks exactly like a model that found no signal.
 *
 * A whitelist, not a blacklist. The previous version named the two constructs
 * known to fail, so the moment the agent reached for a third — log10, sin,
 * floor — the expression passed validation and then quietly evaluated to
 * nothing.
 */
const CHART_FUNCTIONS = new Set([
  'abs',
  'sqrt',
  'exp',
  'log',
  'log10',
  'sin',
  'cos',
  'tan',
  'floor',
  'ceil',
  'round',
  'min',
  'max',
  'pow',
])

/**
 * `clip(x, lo, hi)` → `min(max(x, lo), hi)`, at any nesting depth.
 *
 * A regex cannot do this. The obvious per-argument pattern forbids parentheses
 * inside an argument, so `clip(clip(a, 0, 10), 0, 5)` never matches — not even
 * innermost-first, because unwrapping the inner call leaves the outer one with a
 * parenthesised first argument. The whole pen was then discarded as unchartable.
 * So: scan to the matching bracket and split the arguments at depth zero.
 */
const rewriteClip = (input: string): string => {
  let out = input
  for (let guard = 0; guard < 24; guard++) {
    const call = /\bclip\s*\(/.exec(out)
    if (!call) break
    const open = call.index + call[0].length - 1
    let depth = 0
    let close = -1
    const commas: number[] = []
    for (let i = open; i < out.length; i++) {
      const ch = out[i]
      if (ch === '(') depth++
      else if (ch === ')') {
        depth--
        if (depth === 0) {
          close = i
          break
        }
      } else if (ch === ',' && depth === 1) commas.push(i)
    }
    // Unbalanced, or not the three arguments clip takes: leave it, and the
    // unknown-function check downstream reports it rather than guessing.
    if (close === -1 || commas.length !== 2) break
    const value = out.slice(open + 1, commas[0]).trim()
    const lo = out.slice(commas[0] + 1, commas[1]).trim()
    const hi = out.slice(commas[1] + 1, close).trim()
    out =
      out.slice(0, call.index) +
      `min(max(${value}, ${lo}), ${hi})` +
      out.slice(close + 1)
  }
  return out
}

export const toChartExpression = (
  expr: string
): { expression: string; issue?: string } => {
  let out = expr
    .replaceAll('**', '^') // Python power → math.js power
    .replace(/\bmaximum\s*\(/g, 'max(')
    .replace(/\bminimum\s*\(/g, 'min(')
  out = rewriteClip(out)

  // Anything still shaped like a call has to be something the chart knows.
  const called = new Set<string>()
  for (const match of out.matchAll(/\b([A-Za-z_][A-Za-z0-9_]*)\s*\(/g)) {
    called.add(match[1])
  }
  const unknown = [...called].filter((name) => !CHART_FUNCTIONS.has(name))
  if (unknown.length) {
    return {
      expression: out,
      issue:
        t`the expression calls ${{ value116_34: unknown.map((n) => `${n}()`).join(', ') }}, ` +
        t`which the chart's math cannot evaluate`,
    }
  }
  return { expression: out }
}

export const AGENT_PEN_PREFIX = 'agent-kpi:'

export const agentKpiPenId = (model: Pick<AgentModel, 'entry_id'>): string =>
  `${AGENT_PEN_PREFIX}${model.entry_id}`

/** `((ctim + 459.67) * …)` with roles replaced by their sanitized tag ids. */
export const expressionOverTags = (
  expr: string,
  roles: Record<string, string>
): string => {
  // Longest role names first: `ctd` must not clobber `ctd_sib`.
  const names = Object.keys(roles).sort((a, b) => b.length - a.length)
  let out = expr
  for (const role of names) {
    const tag = roles[role]
    if (!tag) continue
    out = out.replace(
      new RegExp(`\\b${role.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'),
      sanitizeSensorId(tag)
    )
  }
  return out
}

/**
 * The tags the KPI expression actually references — NOT every role. The chart
 * only renders a derived value when *all* of its dependencies are plotted, so
 * listing a driver the expression never uses (or one absent from the site's pen
 * pool) silently suppresses the whole KPI pen.
 */
export const expressionTags = (
  expr: string,
  roles: Record<string, string>
): string[] => {
  const used = new Set<string>()
  for (const [role, tag] of Object.entries(roles)) {
    if (!tag) continue
    const pattern = new RegExp(
      // eslint-disable-next-line lingui/no-unlocalized-strings -- Regular-expression boundary syntax, not user-facing copy.
      `\\b${role.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`
    )
    if (pattern.test(expr)) used.add(tag)
  }
  return [...used]
}

/**
 * The derived-value entry for a model's KPI, or null when the model does not
 * carry enough to build one (an expression and its role→tag map).
 */
export const agentKpiDerivedValue = (
  model: AgentModel,
  siteId: string
): TimeSeriesDerivedValueEntryJSON | null => {
  const roles = model.roles ?? {}
  const expr = model.expr
  if (!expr || !Object.keys(roles).length) return null
  const dependencyIds = expressionTags(expr, roles)
  if (!dependencyIds.length) return null
  const { expression, issue } = toChartExpression(
    // Already sanitized here: the schema's own sanitizer only runs on parse.
    expressionOverTags(expr, roles)
  )
  if (issue) return null
  return {
    id: agentKpiPenId(model),
    type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
    data: {
      name: `${model.kpi ?? 'KPI'} (${model.name ?? 'agent model'})`,
      expression,
      unit: null,
      description: t`Agent model ${{ value193_36: model.entry_id }}`,
      dependencyIds,
    },
    tags: { site: siteId },
  }
}

/** Why this model's KPI cannot be charted, or null when it can. */
export const agentKpiChartIssue = (model: AgentModel): string | null => {
  const roles = model.roles ?? {}
  if (!model.expr || !Object.keys(roles).length) {
    return t`the model carries no KPI expression`
  }
  return toChartExpression(expressionOverTags(model.expr, roles)).issue ?? null
}
